<template>
  <div id="tracking_domain" class="flex flex-col gap-6 proxima_nova">
    <h1 class="text-xl text_color font-semibold">Configure Tracking Domain</h1>
    <form
      @submit.prevent="createSubdomain"
      class="flex flex-col gap-6 secondary_color text-[13px] font-normal"
    >
      <!-- Introduction -->
      <div class="form_col">
        <p class="mb-4">
          Configure a custom tracking domain to enhance your email
          deliverability and maintain brand consistency in your tracking links.
        </p>
      </div>

      <!-- Domain Configuration -->
      <div class="form_col">
        <label for="domain">Domain Name</label>
        <div class="flex gap-2">
          <a-input
            id="domain"
            v-model:value="formState.domain"
            type="text"
            class="input_medium"
            placeholder="e.g. track.yourdomain.com"
            ref="domainNameInputRef"
            allow-clear
          />
          <a-button
            v-if="formState.domain"
            :loading="isValidating"
            class="secondary_medium"
            @click="validateDomain(formState.domain)"
          >
            Validate
          </a-button>
        </div>
        <span class="text-[12px] mt-1">
          Enter a subdomain you'd like to use for tracking. We recommend using
          'track' or 'click' as the subdomain.
        </span>
        <span v-if="errors.domain" class="error-message">
          {{ errors.domain }}
        </span>
      </div>

      <!-- DNS Configuration -->
      <div class="form_col">
        <label>DNS Configuration</label>
        <div class="dns_config_container p-4 rounded-md">
          <p class="mb-2 secondary_color">
            Add the following CNAME record to your DNS settings:
          </p>
          <div class="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p class="text-[12px] mb-1 secondary_color">Type</p>
              <p class="text_color">CNAME</p>
            </div>
            <div>
              <p class="text-[12px] mb-1 secondary_color">Points to</p>
              <p class="text_color">{{ cnameValue }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Documentation -->
      <div class="form_col">
        <label>Setup Instructions</label>
        <div class="flex flex-col gap-3">
          <div>
            <p class="text_color mb-1">1. Choose Your Subdomain</p>
            <p>
              Select a subdomain prefix (e.g., track.yourdomain.com or
              click.yourdomain.com)
            </p>
          </div>
          <div>
            <p class="text_color mb-1">2. Add DNS Record</p>
            <p>
              Add a CNAME record in your DNS settings pointing to our tracking
              server
            </p>
          </div>
          <div>
            <p class="text_color mb-1">3. Verify Configuration</p>
            <p>
              Click the validate button to verify your DNS configuration before
              saving
            </p>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <a-button
        html-type="submit"
        class="primary_medium w-fit"
        :loading="isCreating"
        :disabled="domainStatus === 'failed'"
      >
        Update Domain
      </a-button>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, watch } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";

const formState = reactive({
  domain: "",
});

const errors = reactive({
  domain: "",
});

const isCreating = ref(false);
const isValidating = ref(false);
const trackingDomain = ref("");
const domainStatus = ref("");
const validationMessage = ref("");
const cnameValue = ref("t.superclk.co");
const domainNameInputRef = ref(null);

const validateForm = () => {
  let valid = true;
  if (
    !/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z0-9][\w.-]*[a-zA-Z0-9]$/.test(
      formState.domain
    ) &&
    String(formState.domain).trim().length > 0
  ) {
    errors.domain = "Please enter a valid domain name";
    valid = false;
  } else {
    errors.domain = "";
  }
  return valid;
};

const validateDomain = async (domain) => {
  if (!domain) return;

  try {
    isValidating.value = true;
    const response = await DashboardService.validateDomainTracking(domain);

    if (response.success) {
      domainStatus.value = "verified";
      validationMessage.value =
        "Domain configuration is valid and properly propagated";
      message.success("Domain configuration is valid");
    } else {
      domainStatus.value = "failed";
      validationMessage.value =
        response.message || "Domain configuration is invalid";
      message.error(response.message || "Domain configuration is invalid");
    }
  } catch (error) {
    domainStatus.value = "failed";
    validationMessage.value = "Failed to validate domain configuration";
    message.error("Failed to validate domain configuration");
    console.error("Error validating domain:", error);
  } finally {
    isValidating.value = false;
  }
};

const createSubdomain = async () => {
  if (!validateForm()) {
    message.error("Please correct the errors in the form");
    return;
  }

  try {
    isCreating.value = true;
    const response = await DashboardService.setCnameDomainTracking(
      formState.domain
    );

    if (response.success) {
      trackingDomain.value = formState.domain;
      domainStatus.value = "pending";
      validationMessage.value =
        "Awaiting DNS propagation. Please validate configuration.";
      message.success("Tracking domain configured successfully");
    } else {
      message.error(response.message || "Failed to configure tracking domain");
    }
  } catch (error) {
    console.error("Error creating subdomain:", error);
    message.error("An error occurred while configuring tracking domain");
  } finally {
    isCreating.value = false;
  }
};

const fetchDomainStatus = async () => {
  try {
    const response = await DashboardService.fetchCnameDomainTracking();

    if (response.success) {
      formState.domain = response.domain || "";
      domainStatus.value = response.status || "";
      if (response.message) {
        validationMessage.value = response.message;
      }
    }
  } catch (error) {
    console.error("Error fetching domain status:", error);
  }
};

watch(
  () => formState.domain,
  (newValue) => {
    if (!newValue.trim()) {
      domainStatus.value = "";
    }
  }
);

onMounted(() => {
  if (domainNameInputRef.value) {
    domainNameInputRef.value?.focus();
  }

  fetchDomainStatus();
});
</script>

<style scoped>
.dns_config_container {
  background: var(--black-opacity-15);
}
</style>
